import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Tooltip, Alert } from 'antd';
import {
  InfoCircleOutlined,
  HistoryOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import isEmpty from 'lodash/isEmpty';

import {
  DynamicPropertiesActionsContainer,
  DynamicPropertiesContainer,
  InfoAlertContainer,
  InputStyled,
  TableActionsWrapper,
  TableStyled
} from './styled';
import DynamicPropertiesInfoModal from './InfoModal';
import { CATEGORY_ALL, PAGINATION_DEFAULT } from './constants';

import TopActionItemsComponent from './actionItems';
import { CRS_SETTINGS_TAB_MODULE_ID } from '../../constants/globalConstants';
import { SCREENING_MODULEID_LIST } from '../../../util/GlobalConstant';

const prepareListWithFinalValue = (list, finalFormData = {}) =>
  list.map((item) => ({
    ...item,
    userDefinedValue: item.userDefinedValue || '',
    finalValue:
      (finalFormData[item.id]
        ? finalFormData[item.id].finalValue
        : item.userDefinedValue) || ''
  }));

const prepareSubListWithFinalValue = (list, key, subList = []) =>
  list.map((item) =>
    subList.includes(item.id) ? { ...item, finalValue: item[key] || '' } : item
  );

const DynamicProperties = ({
  className,
  dynProperties,
  dynPropertiesLoading,
  dynPropertiesUpdateSuccess,
  dynPropertiesUpdateData,
  dynPropertiesUpdateError,
  apiActions,
  moduleId,
  subModule,
  typologyId,
  runInstanceId,
  ...props
}) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useState(PAGINATION_DEFAULT);
  const [list, setList] = useState([]);
  const [finalFormData, setFinalFormData] = useState({}); // key = id // value = property record // { [id]: {{property_data}} }
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    data: null
  });
  const [searchValue, setSearchValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(CATEGORY_ALL);
  const [isDataEdited, setIsDataEdited] = useState(false);

  const showActionBtns = useMemo(
    () => selectedRowKeys.length > 0,
    [selectedRowKeys]
  );

  const [modal, contextHolder] = Modal.useModal();

  useEffect(() => {
    setList(
      prepareListWithFinalValue(dynProperties?.data || [], finalFormData)
    );
  }, [dynProperties?.data]);

  const checkIsDataEdited = (data) => {
    setIsDataEdited(() =>
      Object.values(data).some(
        (p) => (p.userDefinedValue || '') !== p.finalValue
      )
    );
  };

  useEffect(() => {
    setFinalFormData((data) => {
      list.forEach((p) => (data[p.id] = p));

      checkIsDataEdited(data);
      return data;
    });
  }, [list]);

  const getDynamicPropertiesPayload = useMemo(
    () => ({
      moduleId,
      subModule,
      typologyId,
      runInstanceId,
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.current - 1),
      engPrefix: searchValue || undefined,
      category: selectedCategory !== CATEGORY_ALL ? selectedCategory : undefined
    }),
    [
      moduleId,
      subModule,
      pagination,
      typologyId,
      runInstanceId,
      searchValue,
      selectedCategory
    ]
  );

  const handleGetDynamicProperties = () => {
    apiActions.getAMLSCommonDynamicProperties(getDynamicPropertiesPayload);
  };

  const handleResetDynamicProperties = () => {
    apiActions.resetAMLSCommonDynamicProperties();
  };

  useEffect(() => handleResetDynamicProperties, []);

  useEffect(() => {
    handleGetDynamicProperties();
  }, [getDynamicPropertiesPayload]);

  const resetFinalFormData = useCallback(() => {
    setFinalFormData(() => ({}));
  }, []);

  useEffect(() => {
    if (dynPropertiesUpdateSuccess) {
      if (dynPropertiesUpdateData.message) {
        modal.info({
          title: 'Dynamic Property Update:',
          icon: <ExclamationCircleOutlined />,
          content: dynPropertiesUpdateData.message,
          okText: 'Ok'
        });
      }
      resetFinalFormData();
      apiActions.putAMLSCommonDynamicPropertiesUpdateReset();
      handleGetDynamicProperties();
    }
  }, [dynPropertiesUpdateSuccess]);

  useEffect(() => {
    if (!isEmpty(dynPropertiesUpdateError)) {
      if (dynPropertiesUpdateError.message) {
        modal.info({
          title: 'ERROR!',
          icon: <ExclamationCircleOutlined />,
          content: dynPropertiesUpdateError.message,
          okText: 'Ok'
        });
      }
      resetFinalFormData();
      apiActions.putAMLSCommonDynamicPropertiesUpdateReset();
      handleGetDynamicProperties();
    }
  }, [dynPropertiesUpdateError]);

  const resetPaginationPage = useCallback(() => {
    setPagination((value) => ({
      ...value,
      current: PAGINATION_DEFAULT.current
    }));
  }, []);

  const handleSelectCategory = useCallback((value) => {
    setSelectedCategory(value);
    resetPaginationPage();
    resetFinalFormData();
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    resetPaginationPage();
    resetFinalFormData();
  }, []);

  const onRowSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const onPaginationChange = (current, pageSize) => {
    setSelectedRowKeys([]); // clearing row selection on page change
    setPagination({ current, pageSize });
  };

  const handleFinalValueChange = useCallback((e, data) => {
    const { value } = e.target;

    setList((list) => {
      const property = list.find((item) => item.id === data.id);
      if (property) {
        property.finalValue = value;
        return [...list];
      }

      return list;
    });
  }, []);

  const handleRestoreFinalValueClick = useCallback((data) => {
    setList((list) => {
      const property = list.find((item) => item.id === data.id);
      if (property) {
        property.finalValue = property.userDefinedValue || '';
        return [...list];
      }

      return list;
    });
  }, []);

  const handleAutoFillDefault = useCallback(() => {
    setList((list) =>
      prepareSubListWithFinalValue(list, 'defaultValue', selectedRowKeys)
    );
  }, [selectedRowKeys]);

  const onAutoFillDefaultClicks = useCallback(() => {
    modal.confirm({
      title: 'Auto Fill Default Values',
      icon: <ExclamationCircleOutlined />,
      content:
        'Are you sure you want to replace the existing values by the default values?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: handleAutoFillDefault
    });
  }, [handleAutoFillDefault]);

  const handleAutoFillRecommended = useCallback(() => {
    setList(
      prepareSubListWithFinalValue(list, 'suggestedValue', selectedRowKeys)
    );
  }, [list, selectedRowKeys]);

  const onAutoFillRecommendedClick = useCallback(() => {
    modal.confirm({
      title: 'Auto Fill Recommended Values',
      icon: <ExclamationCircleOutlined />,
      content:
        'Are you sure you want to replace the existing values by the recommended values?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: handleAutoFillRecommended
    });
  }, [handleAutoFillRecommended]);

  const handleInfoButtonClick = useCallback((data) => {
    setInfoModal({
      isOpen: true,
      data
    });
  }, []);

  const handleInfoModalClose = useCallback(() => {
    setInfoModal({
      isOpen: false,
      data: null
    });
  }, []);

  const handleDiscardChanges = useCallback(() => {
    resetFinalFormData();
    setList((list) => prepareListWithFinalValue(list));
  }, []);

  const onDiscardClick = () => {
    const noOfRecords = Object.values(finalFormData).filter(
      (p) => p.finalValue !== p.userDefinedValue
    ).length;

    modal.confirm({
      title: 'Discard Changes',
      icon: <ExclamationCircleOutlined />,
      content: `Do you really want to discard the changes you made to ${noOfRecords} record(s)?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: handleDiscardChanges
    });
  };

  const handleSaveChanges = useCallback(() => {
    const properties = Object.values(finalFormData)
      .filter((p) => p.finalValue !== p.userDefinedValue)
      .map((p) => ({ id: p.id, name: p.propertyName, value: p.finalValue }));

    apiActions.putAMLSCommonDynamicPropertiesUpdate({
      typologyId,
      runInstanceId,
      moduleId,
      properties
    });
  }, [finalFormData, typologyId, runInstanceId, moduleId]);

  const onSaveClick = () => {
    const noOfRecords = Object.values(finalFormData).filter(
      (p) => p.finalValue !== p.userDefinedValue
    ).length;

    modal.confirm({
      title: 'Save Changes',
      icon: <ExclamationCircleOutlined />,
      content: `Do you really want to save the changes you made to ${noOfRecords} record(s)?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: handleSaveChanges
    });
  };

  const columns = useMemo(
    () => [
      {
        title: 'Property Category',
        dataIndex: 'category'
      },
      {
        title: 'Property Name',
        dataIndex: 'propertyEnglishName'
      },
      {
        title: 'Property Default Value',
        dataIndex: 'defaultValue'
      },
      {
        title: 'Property Recommended Value',
        dataIndex: 'suggestedValue'
      },
      {
        title: 'Final Property Value',
        dataIndex: 'finalValue',
        render: (value, data) => (
          <div>
            <InputStyled
              name="finalValue"
              className="finalValue-input"
              value={value}
              onChange={(e) => handleFinalValueChange(e, data)}
              placeholder="Enter property value"
            />
          </div>
        )
      },
      {
        title: 'Action',
        dataIndex: 'action',
        render: (value, data) => (
          <TableActionsWrapper>
            <Tooltip title="Restore Previous Value">
              <HistoryOutlined
                role="button"
                onClick={() => handleRestoreFinalValueClick(data)}
              />
            </Tooltip>
            <InfoCircleOutlined
              role="button"
              title="Info"
              onClick={() => handleInfoButtonClick(data)}
            />
          </TableActionsWrapper>
        )
      }
    ],
    [handleFinalValueChange, list]
  );

  //Applying the filter to hide the property recommended value column
  const formatedColumns = columns.filter(
    (col) => col.title !== 'Property Recommended Value'
  );

  const disableRecommendedValuesFeature = true;

  return (
    <div>
      <DynamicPropertiesContainer className={className}>
        <TopActionItemsComponent
          moduleId={moduleId}
          subModule={subModule}
          apiActions={apiActions}
          dynPropertiesLoading={dynPropertiesLoading}
          onAutoFillDefault={onAutoFillDefaultClicks}
          onAutoFillRecommended={onAutoFillRecommendedClick}
          showActionBtns={showActionBtns}
          searchValue={searchValue}
          handleSearchChange={handleSearchChange}
          selectedCategory={selectedCategory}
          handleSelectCategory={handleSelectCategory}
          disableRecommendedValueBtn={disableRecommendedValuesFeature}
          {...props}
        />
        <TableStyled
          id="dynamic-properties-table"
          loading={dynPropertiesLoading}
          rowSelection={{
            selectedRowKeys,
            onChange: onRowSelectChange
          }}
          columns={disableRecommendedValuesFeature ? formatedColumns : columns}
          rowKey={'id'}
          dataSource={list}
          pagination={{
            total: dynProperties?.count,
            current: pagination.current,
            pageSize: pagination.pageSize,
            onChange: onPaginationChange,
            showTotal: (total) => <p>{total} Records</p>,
            showSizeChanger: true,
            className: 'dynamic-properties-table-pagination'
          }}
        />
        <DynamicPropertiesActionsContainer>
          <Button
            type="ghost"
            onClick={onDiscardClick}
            disabled={dynPropertiesLoading || !isDataEdited}>
            Discard Changes
          </Button>
          <Button
            type="primary"
            onClick={onSaveClick}
            disabled={dynPropertiesLoading || !isDataEdited}>
            Save
          </Button>
        </DynamicPropertiesActionsContainer>
        {moduleId !== CRS_SETTINGS_TAB_MODULE_ID ? (
          <InfoAlertContainer>
            <Alert
              className="info-alert"
              type="info"
              showIcon
              icon={<InfoCircleOutlined className="info-icon" />}
              message="Any changes applied will not affect a run instance in progress.To push these changes to live, Create a new run instance -> Verify the results -> Push it to live."
            />
          </InfoAlertContainer>
        ) : null}
      </DynamicPropertiesContainer>
      <DynamicPropertiesInfoModal
        visible={infoModal.isOpen}
        propertyData={infoModal.data}
        onCancel={handleInfoModalClose}
      />
      {contextHolder}
    </div>
  );
};

DynamicProperties.propTypes = {
  className: PropTypes.string,
  moduleId: PropTypes.string.isRequired,
  subModule: PropTypes.string.isRequired,
  dynProperties: PropTypes.object,
  dynPropertiesLoading: PropTypes.bool,
  dynPropertiesUpdateSuccess: PropTypes.bool,
  dynPropertiesUpdateData: PropTypes.object,
  dynPropertiesUpdateError: PropTypes.object,
  apiActions: PropTypes.object,
  typologyId: PropTypes.number,
  runInstanceId: PropTypes.string
};

export default DynamicProperties;
